import {
  CircleIcon,
  ClosedCaptioningIcon,
  Icon,
  ImageIcon,
  LineSegmentIcon,
  SelectionBackgroundIcon,
  SquareIcon,
  StampIcon,
  TableIcon,
  TextAaIcon,
  TextTIcon,
  UserCircleIcon,
  VideoCameraIcon,
} from "@phosphor-icons/react";
import {
  Avatar,
  CustomText,
  Header,
  ITEM_TYPES,
  ItemType,
  Logo,
  MediaItem,
  MediaKind,
  ParameteredText,
  SHAPE_TYPES,
  Shape,
  SubtitlesItem,
  Table,
  TemplateState,
  TemplateType,
  TitleItem,
} from "../types";
import { sanitizeText } from "utils/text";

/**
 * Any drawable store item that carries a `layerIndex` and therefore takes part
 * in z-ordering. Mirrors the union assembled in `useChangeLayer.getSortedStoreItems`.
 */
export type LayerStoreItem =
  | MediaItem
  | SubtitlesItem
  | TitleItem
  | CustomText
  | Shape
  | Avatar
  | Logo
  | ParameteredText
  | Table
  | Header;

const I18N_BASE = "components.templates.TemplatesBoard.storyboardLeftPanel";

export type LayerDescriptor = {
  /** Stable id used as the dnd-kit sortable id. */
  id: string;
  /** Canvas item type, used for selection + tab navigation. */
  kind: ItemType["type"];
  /** Raw store item (carries the current layerIndex and matching keys). */
  item: LayerStoreItem;
  layerIndex: number;
  icon: Icon;
  /** Fully-qualified i18n key for the row label (fallback when `name` is absent). */
  nameKey: string;
  /**
   * Literal display name — the item's actual text (custom/parametered content,
   * chapter topic for the title, narration for subtitles). When present it is
   * shown instead of the generic `nameKey` label. Absent for non-text layers
   * and for blank text.
   */
  name?: string;
  /** Payload passed to `setCurrentItem` when the row is clicked. */
  selectItem: ItemType;
  /** Whether the row is shown and reorderable in the layers list. */
  displayable: boolean;
  /** Whether the row exposes a delete action. */
  deletable: boolean;
};

const SHAPE_ICONS: Record<Shape["shapeType"], Icon> = {
  [SHAPE_TYPES.RECTANGLE]: SquareIcon,
  [SHAPE_TYPES.CIRCLE]: CircleIcon,
  [SHAPE_TYPES.LINE]: LineSegmentIcon,
};

export const BACKGROUND_LAYER_ID = "__layer:background";

const itemNameKey = (name: string) => `${I18N_BASE}.itemNames.${name}`;
const shapeNameKey = (shapeType: Shape["shapeType"]) =>
  `${I18N_BASE}.shapeNames.${shapeType}`;

/** Trimmed, single-line preview of a text value; undefined when blank. */
const toDisplayName = (value: string | undefined): string | undefined =>
  sanitizeText(value ?? "") || undefined;

const describeShape = (shape: Shape): LayerDescriptor => ({
  id: shape.id,
  kind: ITEM_TYPES.Shape,
  item: shape,
  layerIndex: shape.layerIndex,
  icon: SHAPE_ICONS[shape.shapeType],
  nameKey: shapeNameKey(shape.shapeType),
  selectItem: {
    type: ITEM_TYPES.Shape,
    id: shape.id,
    shapeType: shape.shapeType,
    orientation: shape.orientation,
  },
  displayable: true,
  deletable: true,
});

const mediaDescriptor = (
  item: MediaItem,
  kind: Extract<ItemType["type"], "Image" | "Video">
): LayerDescriptor => ({
  id: item.id,
  kind,
  item,
  layerIndex: item.layerIndex,
  icon: kind === ITEM_TYPES.Video ? VideoCameraIcon : ImageIcon,
  nameKey: itemNameKey(kind),
  selectItem: { type: kind, id: item.id },
  displayable: true,
  deletable: true,
});

// CHAPTER-only header/logo, in z-order (the caller guards on view).
const leadingChapterDescriptors = (state: TemplateState): LayerDescriptor[] => {
  const descriptors: LayerDescriptor[] = [];
  if (state.header.background) {
    descriptors.push({
      id: "__layer:header",
      kind: ITEM_TYPES.Background,
      item: state.header,
      layerIndex: state.header.layerIndex,
      icon: SelectionBackgroundIcon,
      nameKey: itemNameKey(ITEM_TYPES.Background),
      selectItem: { type: ITEM_TYPES.Background },
      displayable: false,
      deletable: false,
    });
  }
  if (state.logo.showLogo) {
    descriptors.push({
      id: "__layer:logo",
      kind: ITEM_TYPES.Logo,
      item: state.logo,
      layerIndex: state.logo.layerIndex,
      icon: StampIcon,
      nameKey: itemNameKey(ITEM_TYPES.Logo),
      selectItem: { type: ITEM_TYPES.Logo },
      displayable: true,
      deletable: true,
    });
  }
  return descriptors;
};

// Presenter can appear on both chapters and transitions (the caller guards on view).
const presenterDescriptor = (state: TemplateState): LayerDescriptor[] => {
  if (!state.presenter.presenter) return [];
  return [
    {
      id: "__layer:presenter",
      kind: ITEM_TYPES.Presenter,
      item: state.presenter,
      layerIndex: state.presenter.layerIndex,
      icon: UserCircleIcon,
      nameKey: itemNameKey(ITEM_TYPES.Presenter),
      selectItem: { type: ITEM_TYPES.Presenter },
      displayable: true,
      deletable: true,
    },
  ];
};

// Subtitles (chapter) / title (transition) sit at the front of the z-order.
const trailingViewDescriptors = (
  state: TemplateState,
  isChapter: boolean,
  isTransition: boolean,
  context: LayerNameContext
): LayerDescriptor[] => {
  const descriptors: LayerDescriptor[] = [];
  if (isChapter && state.textStyle.showSubtitles) {
    descriptors.push({
      id: "__layer:subtitles",
      kind: ITEM_TYPES.Subtitles,
      item: state.textStyle,
      layerIndex: state.textStyle.layerIndex,
      icon: ClosedCaptioningIcon,
      nameKey: itemNameKey(ITEM_TYPES.Subtitles),
      // Subtitles have no stored text; mirror the canvas, which shows the
      // scene's narration extracted from the storyboard section script.
      name: toDisplayName(context.subtitleText),
      selectItem: { type: ITEM_TYPES.Subtitles },
      displayable: true,
      deletable: true,
    });
  }
  if (isTransition && state.titleStyle.showTitle) {
    descriptors.push({
      id: "__layer:title",
      kind: ITEM_TYPES.Title,
      item: state.titleStyle,
      layerIndex: state.titleStyle.layerIndex,
      icon: TextAaIcon,
      nameKey: itemNameKey(ITEM_TYPES.Title),
      // The title has no stored text; the canvas renders the chapter topic.
      name: toDisplayName(context.chapterTopic),
      selectItem: { type: ITEM_TYPES.Title },
      displayable: true,
      deletable: true,
    });
  }
  return descriptors;
};

/**
 * Text shown for layers whose content lives outside the template state: the
 * chapter topic (rendered as the transition title) and the scene's narration
 * (rendered as subtitles). Resolved by the consumer and passed in.
 */
export type LayerNameContext = {
  chapterTopic?: string;
  subtitleText?: string;
  /**
   * The main media's kind, resolved from the slide when `mainImage.mediaType`
   * is unset (the default/original media). Lets the row show a Video icon/name
   * for a video main image. See `resolveMainImageMediaType`.
   */
  mainImageMediaType?: MediaKind;
};

/**
 * Builds the ordered list of layer descriptors for the active scene, applying
 * the same visibility rules as `useChangeLayer.getSortedStoreItems` so the
 * layers list and the reorder mechanics stay in lockstep.
 *
 * Items are returned ascending by `layerIndex` (back-to-front). The header is
 * included (so global reindexing on reorder stays collision-free) but flagged
 * `displayable: false` — it is managed via the position controls, not the list.
 * The body background is intentionally absent: it is rendered as a separate
 * pinned, non-reorderable row by the consumer.
 */
export const getLayerDescriptors = (
  state: TemplateState,
  view: TemplateType,
  context: LayerNameContext = {}
): LayerDescriptor[] => {
  const isChapter = view === TemplateType.CHAPTER;
  const isTransition = view === TemplateType.TRANSITION;
  const descriptors: LayerDescriptor[] = [];

  if (isChapter) descriptors.push(...leadingChapterDescriptors(state));
  if (isChapter || isTransition) descriptors.push(...presenterDescriptor(state));

  state.images.forEach((image) =>
    descriptors.push(mediaDescriptor(image, ITEM_TYPES.Image))
  );

  state.videos.forEach((video) =>
    descriptors.push(mediaDescriptor(video, ITEM_TYPES.Video))
  );

  state.customTexts.forEach((text) => {
    descriptors.push({
      id: text.id,
      kind: ITEM_TYPES.CustomText,
      item: text,
      layerIndex: text.layerIndex,
      icon: TextTIcon,
      nameKey: itemNameKey(ITEM_TYPES.CustomText),
      name: toDisplayName(text.content),
      selectItem: { type: ITEM_TYPES.CustomText, id: text.id },
      displayable: true,
      deletable: true,
    });
  });

  // Hidden parametered texts (visible: false) are treated as deleted and
  // omitted from the list.
  state.parameteredTexts
    ?.filter((text) => text.visible !== false)
    .forEach((text) => {
      descriptors.push({
        id: text.id,
        kind: ITEM_TYPES.ParameteredText,
        item: text,
        layerIndex: text.layerIndex,
        icon: TextTIcon,
        nameKey: itemNameKey(ITEM_TYPES.ParameteredText),
        name: toDisplayName(text.content),
        selectItem: { type: ITEM_TYPES.ParameteredText, id: text.id },
        displayable: true,
        deletable: true,
      });
    });

  const figureParts = [state.figure?.number, state.figure?.units].filter(
    (part): part is ParameteredText => !!part && part.visible !== false
  );
  figureParts.forEach((part) => {
    descriptors.push({
      id: part.id,
      kind: ITEM_TYPES.ParameteredText,
      item: part,
      layerIndex: part.layerIndex,
      icon: TextTIcon,
      nameKey: itemNameKey(ITEM_TYPES.ParameteredText),
      name: toDisplayName(part.content),
      selectItem: { type: ITEM_TYPES.ParameteredText, id: part.id },
      displayable: true,
      deletable: true,
    });
  });

  if (state.mainImage) {
    // The default main media has no stored mediaType; fall back to the slide's
    // resolved kind (passed via context) so a video main image shows as Video.
    const mediaType = state.mainImage.mediaType ?? context.mainImageMediaType;
    const kind = mediaType === "video" ? ITEM_TYPES.Video : ITEM_TYPES.Image;
    descriptors.push(mediaDescriptor(state.mainImage, kind));
  }

  // Layout shapes come from the theme but are still deletable (deleteShape
  // handles both regular and layout shapes).
  state.slideLayoutShapes?.forEach((shape) => {
    descriptors.push(describeShape(shape));
  });

  if (state.table) {
    descriptors.push({
      id: state.table.id,
      kind: ITEM_TYPES.Table,
      item: state.table,
      layerIndex: state.table.layerIndex,
      icon: TableIcon,
      nameKey: itemNameKey(ITEM_TYPES.Table),
      selectItem: { type: ITEM_TYPES.Table, id: state.table.id },
      displayable: true,
      deletable: true,
    });
  }

  state.shapes.forEach((shape) => {
    descriptors.push(describeShape(shape));
  });

  descriptors.push(
    ...trailingViewDescriptors(state, isChapter, isTransition, context)
  );

  return descriptors.toSorted((a, b) => a.layerIndex - b.layerIndex);
};

/**
 * Descriptor for the scene's body background — always the bottom-most layer,
 * rendered as a fixed (non-reorderable, non-deletable) row by the consumer.
 */
export const getBackgroundDescriptor = (
  background: TemplateState["background"]
): LayerDescriptor => ({
  id: BACKGROUND_LAYER_ID,
  kind: ITEM_TYPES.Background,
  // The body background is not a layerIndex-bearing item; it is never
  // reordered, so the raw value is carried only for completeness.
  item: background as unknown as LayerStoreItem,
  layerIndex: 0,
  icon: SelectionBackgroundIcon,
  nameKey: itemNameKey(ITEM_TYPES.Background),
  selectItem: { type: ITEM_TYPES.Background },
  displayable: true,
  deletable: false,
});
